import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Box, Button, Typography, CircularProgress, LinearProgress } from '@mui/material';
import { Mic as MicIcon, Stop as StopIcon } from '@mui/icons-material';
import api from '../services/api';

/**
 * VoiceTranscribe — a compact record-and-transcribe button.
 *
 * Two modes:
 *   1. Project-level (default):       pass projectId. Audio goes to
 *      POST /api/projects/:id/voice-note?transcribe_only=true and the text
 *      is returned via onTranscribed(text) for the parent to handle.
 *
 *   2. Task-level (Field Ops v1 #4): pass taskId (and optionally autoSave).
 *      Audio goes to POST /api/field-ops/tasks/:taskId/voice-note. If
 *      autoSave is true (default for task mode), the server persists the
 *      transcript as a task_notes row with is_voice=true. The parent
 *      receives the saved note via onSaved(note) AND the transcript via
 *      onTranscribed(text).
 *
 * Props:
 *   projectId — project ID for the project-level endpoint
 *   taskId — task ID for the field-ops task voice-note endpoint
 *   autoSave — in task mode, persist on the server (default true)
 *   onTranscribed(text) — always called with the transcript on success
 *   onSaved(note) — called in task mode when autoSave persisted the row
 *   label — button label (default: "Voice Note")
 *   compact — if true, hides the label
 */
export default function VoiceTranscribe({
  projectId,
  taskId,
  autoSave,
  onTranscribed,
  onSaved,
  label = 'Voice Note',
  compact = false,
}) {
  // Default autoSave behaviour: ON for task mode, OFF for project mode.
  const shouldAutoSave = autoSave === undefined ? !!taskId : autoSave;
  const [phase, setPhase] = useState('idle'); // idle | recording | transcribing | error
  const [elapsed, setElapsed] = useState(0);
  const [errorMsg, setErrorMsg] = useState('');

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    return () => {
      clearInterval(timerRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    };
  }, []);

  const reset = useCallback(() => {
    clearInterval(timerRef.current);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    mediaRecorderRef.current = null;
    chunksRef.current = [];
    setElapsed(0);
  }, []);

  const startRecording = async () => {
    try {
      setErrorMsg('');
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/mp4')
        ? 'audio/mp4'
        : 'audio/webm';

      const recorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = async () => {
        clearInterval(timerRef.current);
        stream.getTracks().forEach(t => t.stop());
        setPhase('transcribing');

        const ext = mimeType.includes('mp4') ? 'mp4' : 'webm';
        const blob = new Blob(chunksRef.current, { type: mimeType });

        if (blob.size < 1000) {
          setPhase('error');
          setErrorMsg('Recording too short. Try again.');
          reset();
          return;
        }

        try {
          const formData = new FormData();
          formData.append('audio', blob, `voice_note.${ext}`);

          // Pick the right endpoint based on mode.
          let url;
          if (taskId) {
            url = `/field-ops/tasks/${taskId}/voice-note${
              shouldAutoSave ? '' : '?transcribe_only=true'
            }`;
          } else {
            url = `/projects/${projectId}/voice-note?transcribe_only=true`;
          }

          const res = await api.post(url, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 90000,
          });

          const transcription =
            res.data?.transcription || res.data?.details || '';
          if (transcription && onTranscribed) {
            onTranscribed(transcription);
          }
          if (res.data?.note && onSaved) {
            onSaved(res.data.note);
          }
          setPhase('idle');
          reset();
        } catch (err) {
          console.error('Transcription error:', err);
          setPhase('error');
          setErrorMsg('Transcription failed. Try typing instead.');
          reset();
        }
      };

      recorder.start(1000);
      setPhase('recording');
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed(s => s + 1), 1000);
    } catch (err) {
      setPhase('error');
      setErrorMsg('Microphone access denied. Check browser permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
  };

  const handleClick = () => {
    if (phase === 'idle' || phase === 'error') {
      startRecording();
    } else if (phase === 'recording') {
      stopRecording();
    }
  };

  const formatTime = (s) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;

  if (phase === 'transcribing') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={16} />
        <Typography variant="caption" color="text.secondary">Transcribing...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Button
        variant={phase === 'recording' ? 'contained' : 'outlined'}
        color={phase === 'recording' ? 'error' : 'primary'}
        size="small"
        startIcon={phase === 'recording' ? <StopIcon /> : <MicIcon />}
        onClick={handleClick}
        sx={{ textTransform: 'none', minWidth: compact ? 40 : undefined }}
      >
        {compact ? '' : phase === 'recording' ? `Stop (${formatTime(elapsed)})` : label}
      </Button>
      {phase === 'recording' && (
        <LinearProgress
          sx={{ flex: 1, minWidth: 40, height: 4, borderRadius: 2 }}
          variant="indeterminate"
          color="error"
        />
      )}
      {errorMsg && (
        <Typography variant="caption" color="error">{errorMsg}</Typography>
      )}
    </Box>
  );
}
